import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { API_V1_URL } from '../config';
import { LEARNING_URL } from '../utils/learningUrl';
import '../styles/LandingPage.css';

/* ── Static Data ─────────────────────────────────────────────── */
const LEARNING_FEATURES = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'AI Code Reviews',
    desc: 'Every submission gets deep analysis — correctness, efficiency, quality, readability, and security scored instantly.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: '15+ Languages',
    desc: 'Python, Java, C++, JavaScript, Go, Rust, TypeScript — practice in the language that matters to you.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    title: 'Progress Dashboard',
    desc: 'Visual stats, weekly streaks, score trends, and submission history — everything in one place.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Timed Challenges',
    desc: 'Simulate real interview pressure with configurable timers. Build the speed and accuracy interviewers look for.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    title: 'Instant Test Validation',
    desc: 'Run against visible and hidden test cases. See exactly where your solution passes or fails.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Curated Problem Set',
    desc: 'Easy to Hard — problems designed to cover every key algorithm and data structure topic.',
  },
];

const INTERNSHIP_STAGES = [
  { num: '01', title: 'Enroll & Setup',             desc: 'Pick your project, set up your local environment & repository' },
  { num: '02', title: 'Build Stage-by-Stage',       desc: 'Complete each project stage at your own pace with guided instructions' },
  { num: '03', title: 'Get Admin Reviews',           desc: 'Submit your work and receive detailed feedback on every stage' },
  { num: '04', title: 'Earn Your Certificate',       desc: 'Complete all stages and get a verified completion certificate' },
];

// TODO: Fetch real stats from backend analytics API
// const STATS = [
//   { value: '1K+', label: 'Active Learners' },
//   { value: '5K+', label: 'Code Reviews' },
//   { value: '200+', label: 'Problems' },
//   { value: '95%',  label: 'User Satisfaction' },
// ];

const HOW_LEARNING = [
  {
    step: '01',
    icon: '🚀',
    title: 'Sign Up & Start Free',
    desc: 'Create your account in seconds. Get 3 free AI-reviewed submissions — no payment needed.',
    accent: '#667eea',
  },
  {
    step: '02',
    icon: '🎯',
    title: 'Pick Your Challenge',
    desc: 'Choose from 200+ curated problems filtered by difficulty, topic, and language.',
    accent: '#764ba2',
  },
  {
    step: '03',
    icon: '⚡',
    title: 'Code & Test',
    desc: 'Write code in our editor with 15+ languages. Run against visible & hidden test cases instantly.',
    accent: '#f59e0b',
  },
  {
    step: '04',
    icon: '🤖',
    title: 'Get AI Review',
    desc: 'Receive detailed scores on correctness, efficiency, readability & security — with fix suggestions.',
    accent: '#10b981',
  },
  {
    step: '05',
    icon: '📈',
    title: 'Track & Improve',
    desc: 'Monitor your progress dashboard — streaks, score trends, and topic mastery over time.',
    accent: '#ec4899',
  },
];

const HOW_INTERNSHIP = [
  {
    step: '01',
    icon: '💳',
    title: 'Enroll & Choose Project',
    desc: 'Complete one-time payment. Pick from real-world projects — e-commerce, social apps, APIs, and more.',
    accent: '#10b981',
  },
  {
    step: '02',
    icon: '🛠️',
    title: 'Setup & Build',
    desc: 'Set up your dev environment, design the database schema, and start building your project from scratch.',
    accent: '#3b82f6',
  },
  {
    step: '03',
    icon: '📝',
    title: 'Submit Each Stage',
    desc: 'Complete each progressive stage — from setup to final submission. Upload your work for each stage.',
    accent: '#8b5cf6',
  },
  {
    step: '04',
    icon: '👨‍💻',
    title: 'Get Admin Review',
    desc: 'Our team reviews your submission with detailed feedback. Fix issues and resubmit if needed.',
    accent: '#f59e0b',
  },
  {
    step: '05',
    icon: '🏆',
    title: 'Earn Certificate',
    desc: 'Complete all stages and receive a verified internship certificate — real code, real experience.',
    accent: '#ef4444',
  },
];

// TODO: Replace with real testimonials from users once we have them
const TESTIMONIALS = [
  {
    name: 'Student',
    role: 'Learning Track User',
    avatar: 'ST',
    color: '#667eea',
    quote: 'The AI-powered code reviews helped me understand where I was going wrong. The detailed feedback on correctness, efficiency, and code quality is invaluable.',
  },
  {
    name: 'Intern',
    role: 'Internship Programme',
    avatar: 'IN',
    color: '#10b981',
    quote: 'The structured internship gave me hands-on experience with the full development lifecycle. Building a real project was the best learning experience.',
  },
  {
    name: 'Developer',
    role: 'Platform User',
    avatar: 'DV',
    color: '#f59e0b',
    quote: 'Being able to practice in multiple languages with instant AI feedback accelerated my learning. The progress tracking keeps me motivated.',
  },
];

/* ── Component ───────────────────────────────────────────────── */
const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('internship');
  const [scrolled, setScrolled] = useState(false);
  const [pricing, setPricing] = useState({
    learningPerSubmission: 99,
    learningMonthly: 499,
    internship: 1499,
    internshipOriginal: 2999,
    freeTrialSubmissions: 3,
    currencySymbol: '₹'
  });

  // Fetch pricing from backend configuration
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_V1_URL}/config/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            learningPerSubmission: data.learning_per_submission,
            learningMonthly: data.learning_monthly,
            internship: data.internship,
            internshipOriginal: data.internship_original,
            freeTrialSubmissions: data.free_trial_submissions,
            currencySymbol: data.currency_symbol
          });
        }
      } catch (error) {
        console.log('Using default pricing:', error.message);
      }
    };
    fetchPricing();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/internship/projects', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Preserve UTM params from the current URL for all /register links
  const utmString = (() => {
    const params = new URLSearchParams(window.location.search);
    const utm = new URLSearchParams();
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach(k => {
      if (params.get(k)) utm.set(k, params.get(k));
    });
    const str = utm.toString();
    return str ? `?${str}` : '';
  })();

  const registerLink = `/register${utmString}`;

  if (isAuthenticated) return null;

  return (
    <div className="lp-root">

      {/* ── Navbar ──────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? ' lp-nav--scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <div className="lp-nav-brand">
            <img src="/final_logo.png" alt="Batos CodeHub" className="lp-nav-logo" />
          </div>
          <div className="lp-nav-links">
            <a href="#programs" className="lp-nav-link">Programs</a>
            <a href="#how-it-works" className="lp-nav-link">How It Works</a>
            <a href="#internship" className="lp-nav-link">Internship</a>
          </div>
          <div className="lp-nav-actions">
            <Link to="/login" className="lp-nav-signin">Sign In</Link>
            <Link to={registerLink} className="lp-nav-cta">Get Started</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-blob lp-hero-blob--1" />
          <div className="lp-hero-blob lp-hero-blob--2" />
          <div className="lp-hero-grid" />
        </div>

        <div className="lp-hero-content">
          {/* Center headline */}
          <div className="lp-hero-headline">
            <div className="lp-hero-badge">
              <span className="lp-hero-badge-dot" />
              AI-Powered Coding Platform
            </div>

            <h1 className="lp-hero-h1">
              Learn. Build.<br />
              <span className="lp-hero-accent">Get Hired.</span>
            </h1>

            <p className="lp-hero-sub">
              Two powerful paths to become job-ready — sharpen your coding skills with AI-powered 
              practice, or gain real-world experience through structured project internships.
            </p>
          </div>

          {/* Dual Visual Panels */}
          <div className="lp-hero-dual">
            {/* Learning Panel */}
            <div className="lp-hero-panel lp-hero-panel--learn" style={{ position: 'relative' }}>
              <div className="lp-panel-label">
                <span className="lp-panel-label-icon">📚</span>
                <span className="lp-panel-label-text">Learning Track</span>
                <span className="lp-panel-label-badge lp-panel-label-badge--trial">Free Trial</span>
              </div>
              <div className="lp-code-window">
                <div className="lp-cw-header">
                  <div className="lp-cw-dots">
                    <span className="lp-dot lp-dot--red" />
                    <span className="lp-dot lp-dot--yellow" />
                    <span className="lp-dot lp-dot--green" />
                  </div>
                  <span className="lp-cw-title">solution.py</span>
                  <span className="lp-cw-score">AI Score: 95/100</span>
                </div>
                <div className="lp-cw-body">
                  <pre className="lp-code">{`def two_sum(nums, target):
    seen = {}
    for i, num in enumerate(nums):
        complement = target - num
        if complement in seen:
            return [seen[complement], i]
        seen[num] = i
    return []`}</pre>
                </div>
                <div className="lp-cw-review">
                  <div className="lp-cw-review-row">
                    <span>Correctness</span>
                    <div className="lp-cw-bar"><div className="lp-cw-fill" style={{width:'98%', background:'#10b981'}} /></div>
                    <span>98</span>
                  </div>
                  <div className="lp-cw-review-row">
                    <span>Efficiency</span>
                    <div className="lp-cw-bar"><div className="lp-cw-fill" style={{width:'95%', background:'#667eea'}} /></div>
                    <span>95</span>
                  </div>
                  <div className="lp-cw-review-row">
                    <span>Readability</span>
                    <div className="lp-cw-bar"><div className="lp-cw-fill" style={{width:'90%', background:'#f59e0b'}} /></div>
                    <span>90</span>
                  </div>
                </div>
              </div>
              <div className="lp-panel-features">
                <span>✦ 200+ Problems</span>
                <span>✦ 15+ Languages</span>
                <span>✦ AI Reviews</span>
              </div>
              <Link to={registerLink} className="lp-panel-cta lp-panel-cta--learn">
                Start Learning Free
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>

            {/* Divider */}
            <div className="lp-hero-divider">
              <div className="lp-hero-divider-line" />
              <span className="lp-hero-divider-or">OR</span>
              <div className="lp-hero-divider-line" />
            </div>

            {/* Internship Panel */}
            <div className="lp-hero-panel lp-hero-panel--intern">
              <div className="lp-panel-label">
                <span className="lp-panel-label-icon">🎓</span>
                <span className="lp-panel-label-text">Internship Track</span>
                <span className="lp-panel-label-badge lp-panel-label-badge--paid">{pricing.currencySymbol}{pricing.internship.toLocaleString('en-IN')}</span>
              </div>
              <div className="lp-project-window">
                <div className="lp-pw-header">
                  <div className="lp-cw-dots">
                    <span className="lp-dot lp-dot--red" />
                    <span className="lp-dot lp-dot--yellow" />
                    <span className="lp-dot lp-dot--green" />
                  </div>
                  <span className="lp-cw-title">E-Commerce API Project</span>
                  <span className="lp-pw-status">In Progress</span>
                </div>
                <div className="lp-pw-stages">
                  <div className="lp-pw-stage lp-pw-stage--done">
                    <div className="lp-pw-stage-check">✓</div>
                    <div className="lp-pw-stage-info">
                      <span className="lp-pw-stage-name">Stage 1 · Project Setup</span>
                      <span className="lp-pw-stage-status">Auto-approved</span>
                    </div>
                  </div>
                  <div className="lp-pw-stage lp-pw-stage--done">
                    <div className="lp-pw-stage-check">✓</div>
                    <div className="lp-pw-stage-info">
                      <span className="lp-pw-stage-name">Stage 2 · Database Design</span>
                      <span className="lp-pw-stage-status">Auto-approved</span>
                    </div>
                  </div>
                  <div className="lp-pw-stage lp-pw-stage--done">
                    <div className="lp-pw-stage-check">✓</div>
                    <div className="lp-pw-stage-info">
                      <span className="lp-pw-stage-name">Stage 3 · API Development</span>
                      <span className="lp-pw-stage-status">Auto-approved</span>
                    </div>
                  </div>
                  <div className="lp-pw-stage lp-pw-stage--current">
                    <div className="lp-pw-stage-dot" />
                    <div className="lp-pw-stage-info">
                      <span className="lp-pw-stage-name">Stage 4 · Testing & Deploy</span>
                      <span className="lp-pw-stage-status lp-pw-stage-status--review">Admin Review</span>
                    </div>
                  </div>
                </div>
                <div className="lp-pw-footer">
                  <div className="lp-pw-progress">
                    <div className="lp-pw-progress-bar">
                      <div className="lp-pw-progress-fill" style={{width: '75%'}} />
                    </div>
                    <span className="lp-pw-progress-text">75% Complete</span>
                  </div>
                  <span className="lp-pw-cert">🏆 Certificate on completion</span>
                </div>
              </div>
              <div className="lp-panel-features">
                <span>✦ Real Projects</span>
                <span>✦ Admin Review</span>
                <span>✦ Certificate</span>
              </div>
              <Link to={registerLink} className="lp-panel-cta lp-panel-cta--intern">
                Enroll in Internship
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>

          {/* Bottom explore link */}
          <div className="lp-hero-bottom">
            <a href="#programs" className="lp-btn-ghost">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Explore Both Programs
            </a>
          </div>
        </div>
      </section>

      {/* TODO: Enable trust bar once we have verified users from these institutions */}
      {/* <div className="lp-trust-bar">
        <span className="lp-trust-label">Trusted by students from</span>
        {['IIT Delhi', 'NIT Pune', 'VIT Vellore', 'BITS Pilani', 'IIT Bombay'].map(uni => (
          <span key={uni} className="lp-trust-uni">{uni}</span>
        ))}
      </div> */}

      {/* ── Programs Section ─────────────────────────────────── */}
      <section className="lp-programs" id="programs">
        <div className="lp-section-wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow">Our Programs</span>
            <h2 className="lp-section-h2">Two paths, one goal</h2>
            <p className="lp-section-desc">
              Whether you want to sharpen your interview skills or gain real-world project experience,
              we have a structured programme for you.
            </p>
          </div>

          <div className="lp-programs-grid">
            {/* Learning Card */}
            <div className="lp-prog-card lp-prog-card--learn" style={{ position: 'relative' }}>
              <div className="lp-prog-card-top">
                <div className="lp-prog-icon lp-prog-icon--learn">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="lp-prog-meta">
                  <span className="lp-prog-type">Learning Track</span>
                  <div className="lp-prog-price-wrap">
                    <span className="lp-prog-price lp-prog-price--trial">Free Trial</span>
                    <span className="lp-prog-price-sub">then {pricing.currencySymbol}{pricing.learningPerSubmission}/submission or {pricing.currencySymbol}{pricing.learningMonthly}/mo</span>
                  </div>
                </div>
              </div>
              <h3 className="lp-prog-title">Master Algorithms &amp; Data Structures</h3>
              <p className="lp-prog-desc">
                Solve hundreds of curated problems with AI-powered feedback on every submission.
                Build the skills that top companies look for.
              </p>
              <ul className="lp-prog-list">
                {['3 free AI-reviewed submissions to start', '200+ curated problems', 'AI review on every submission', 'Correctness, speed & quality scores', 'Progress dashboard & streaks', '15+ programming languages', 'Submission history & analysis'].map(item => (
                  <li key={item}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#667eea" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to={registerLink} className="lp-prog-btn lp-prog-btn--learn">
                Start Free Trial
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <p className="lp-prog-trial-note">3 free AI-reviewed submissions included</p>
            </div>

            {/* Internship Card */}
            <div className="lp-prog-card lp-prog-card--intern" id="internship">
              <div className="lp-prog-popular">⭐ Most Popular</div>
              <div className="lp-prog-card-top">
                <div className="lp-prog-icon lp-prog-icon--intern">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="lp-prog-meta">
                  <span className="lp-prog-type">Internship Track</span>
                  <div className="lp-prog-price-wrap">
                    <span className="lp-prog-price lp-prog-price--paid">{pricing.currencySymbol}{pricing.internship.toLocaleString('en-IN')}</span>
                    <span className="lp-prog-strike">{pricing.currencySymbol}{pricing.internshipOriginal.toLocaleString('en-IN')}</span>
                    <span className="lp-prog-save">50% off</span>
                  </div>
                </div>
              </div>
              <h3 className="lp-prog-title">Real-World Project Internship</h3>
              <p className="lp-prog-desc">
                Build a complete production-grade project across multiple guided stages with admin reviews.
                Walk away with a verified certificate and real code to show.
              </p>
              <ul className="lp-prog-list lp-prog-list--intern">
                {['Multi-stage guided project', 'Admin review on every stage', 'Verified completion certificate', 'Full tech stack — setup to submission', 'Self-paced, no deadlines', '6 months access to project'].map(item => (
                  <li key={item}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to={registerLink} className="lp-prog-btn lp-prog-btn--intern">
                Enroll in Internship →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Internship Stages ─────────────────────────────────── */}
      <section className="lp-stages">
        <div className="lp-section-wrap">
          <div className="lp-stages-inner">
            <div className="lp-stages-left">
              <span className="lp-eyebrow lp-eyebrow--green">Internship Programme</span>
              <h2 className="lp-section-h2">Your Path to<br/>Job-Readiness</h2>
              <p className="lp-section-desc" style={{ marginBottom: '32px' }}>
                Each stage mirrors what you'd do in a real engineering team. Submit your work,
                get admin review, and move to the next stage at your own pace.
              </p>
              <div className="lp-stages-cta">
                <Link to={registerLink} className="lp-btn-primary lp-btn-primary--green">
                  Enroll Now — {pricing.currencySymbol}{pricing.internship.toLocaleString('en-IN')}
                </Link>
                <span className="lp-stages-note">One-time payment · Self-paced · Certificate included</span>
              </div>
            </div>

            <div className="lp-stages-right">
              {INTERNSHIP_STAGES.map((s, i) => (
                <div key={s.num} className="lp-stage-row" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="lp-stage-num">{s.num}</div>
                  <div className="lp-stage-body">
                    <div className="lp-stage-title">{s.title}</div>
                    <div className="lp-stage-desc">{s.desc}</div>
                  </div>
                  <svg className="lp-stage-check" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Learning Features ─────────────────────────────────── */}
      <section className="lp-features">
        <div className="lp-section-wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow">Learning Features</span>
            <h2 className="lp-section-h2">Everything you need to excel</h2>
            <p className="lp-section-desc">Professional-grade tools designed to accelerate your coding journey</p>
          </div>

          <div className="lp-features-grid">
            {LEARNING_FEATURES.map(f => (
              <div key={f.title} className="lp-feat-card">
                <div className="lp-feat-icon">{f.icon}</div>
                <h4 className="lp-feat-title">{f.title}</h4>
                <p className="lp-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Demo Video ───────────────────────────────────────── */}
      <section className="lp-demo-video">
        <div className="lp-section-wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow lp-eyebrow--green">See It in Action</span>
            <h2 className="lp-section-h2">Watch the Internship Demo</h2>
            <p className="lp-section-desc">
              See how students build real-world projects, submit each stage, and earn their certificate.
            </p>
          </div>
          <div className="lp-demo-video-wrap">
            <iframe
              src="https://www.youtube.com/embed/0zqxrowp3QI"
              title="Batos CodeHub Internship Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section className="lp-how" id="how-it-works">
        <div className="lp-section-wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow">How It Works</span>
            <h2 className="lp-section-h2">Your journey, step by step</h2>
            <p className="lp-section-desc">
              Choose your path — practice coding problems or build a real-world project. Here's what each journey looks like.
            </p>
          </div>

          {/* Path Toggle */}
          <div className="lp-how-toggle-wrap">
            <button
              className={`lp-how-toggle-btn ${activeTab === 'learning' ? 'lp-how-toggle-btn--active' : ''}`}
              onClick={() => setActiveTab('learning')}
            >
              <span className="lp-how-toggle-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              Learning Track
            </button>
            <button
              className={`lp-how-toggle-btn lp-how-toggle-btn--intern ${activeTab === 'internship' ? 'lp-how-toggle-btn--active' : ''}`}
              onClick={() => setActiveTab('internship')}
            >
              <span className="lp-how-toggle-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              Internship Track
            </button>
          </div>

          {/* Journey Timeline */}
          <div className="lp-how-journey" key={activeTab}>
            <div className="lp-how-timeline-line" />
            {(activeTab === 'learning' ? HOW_LEARNING : HOW_INTERNSHIP).map((h, i) => (
              <div
                key={h.step}
                className={`lp-how-journey-step ${i % 2 === 0 ? 'lp-how-journey-step--left' : 'lp-how-journey-step--right'}`}
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <div className="lp-how-journey-node" style={{ background: h.accent, boxShadow: `0 0 0 6px ${h.accent}20, 0 4px 14px ${h.accent}40` }}>
                  <span className="lp-how-journey-num">{h.step}</span>
                </div>
                <div className="lp-how-journey-card">
                  <div className="lp-how-journey-emoji">{h.icon}</div>
                  <h4 className="lp-how-journey-title">{h.title}</h4>
                  <p className="lp-how-journey-desc">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="lp-how-bottom-cta">
            {activeTab === 'learning' ? (
              <Link to={registerLink} className="lp-btn-primary">
                Start Learning Free — 3 AI Reviews Included
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            ) : (
              <Link to={registerLink} className="lp-btn-primary lp-btn-primary--green">
                Enroll in Internship — {pricing.currencySymbol}{pricing.internship.toLocaleString('en-IN')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────── */}
      <section className="lp-testimonials">
        <div className="lp-section-wrap">
          <div className="lp-section-head">
            <span className="lp-eyebrow">Success Stories</span>
            <h2 className="lp-section-h2">Real results, real people</h2>
          </div>

          <div className="lp-testi-grid">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="lp-testi-card">
                <div className="lp-testi-stars">
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b">
                      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                    </svg>
                  ))}
                </div>
                <p className="lp-testi-quote">"{t.quote}"</p>
                <div className="lp-testi-author">
                  <div className="lp-testi-avatar" style={{ background: t.color }}>{t.avatar}</div>
                  <div>
                    <div className="lp-testi-name">{t.name}</div>
                    <div className="lp-testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────── */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <div className="lp-cta-blob lp-cta-blob--1" />
          <div className="lp-cta-blob lp-cta-blob--2" />
          <span className="lp-eyebrow lp-eyebrow--white">Get Started Today</span>
          <h2 className="lp-cta-h2">Your next offer starts here</h2>
          <p className="lp-cta-desc">
            Start building real-world projects, gain practical experience, and earn a verified certificate.
          </p>
          <div className="lp-cta-actions">
            <Link to={registerLink} className="lp-btn-primary lp-btn-primary--white">
              Enroll in Internship
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/login" className="lp-btn-outline-white">Sign In</Link>
          </div>
          <p className="lp-cta-note">One-time payment · Build real projects · Earn a certificate</p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-footer-logo-wrap">
              <img src="/final_logo.png" alt="Batos CodeHub" className="lp-footer-logo" />
              <span className="lp-footer-name">Batos CodeHub</span>
            </div>
            <p className="lp-footer-tagline">
              AI-powered coding practice platform. Build skills, build confidence, get hired.
            </p>
            <div className="lp-footer-socials">
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="lp-social-link" aria-label="LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://github.com" target="_blank" rel="noreferrer" className="lp-social-link" aria-label="GitHub">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="lp-footer-cols">
            <div className="lp-footer-col">
              <h5>Platform</h5>
              <a href="#programs">Learning Track</a>
              <a href="#internship">Internship Track</a>
              <a href="#how-it-works">How It Works</a>
              <Link to="/login">Sign In</Link>
            </div>
            <div className="lp-footer-col">
              <h5>Resources</h5>
              <a href={LEARNING_URL}>Learning</a>
              <a href="#programs">Pricing</a>
              <Link to="/faq">FAQ</Link>
              <Link to="/contact-us">Contact Us</Link>
            </div>
          </div>
          
          <div className="lp-footer-col">
            <h5>Legal</h5>
            <Link to="/terms">Terms & Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/refund-policy">Refund Policy</Link>
            <Link to="/cancellation-policy">Cancellation Policy</Link>
            <Link to="/contact-us">Contact Us</Link>
          </div>
        </div>

        <div className="lp-footer-bottom">
          <p>© 2026 Batos CodeHub. All rights reserved.</p>
          <p>Made with ❤️ for developers everywhere</p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;